import { Base } from '@studiometa/js-toolkit';
import VariantRadios from './VariantRadios.js';
import PickupAvailability from './PickupAvailability.js';
import PickupAvailabilityDrawer from './PickupAvailabilityDrawer.js';
import Select from '../../../Select.js';
import UpsellProduct from './UpsellProduct.js';

const { subscribe, publish, PUB_SUB_EVENTS } = window;

/**
 * ProductInfo
 */
export default class ProductInfo extends Base {
  cartUpdateUnsubscriber = undefined;

  variantChangeUnsubscriber = undefined;

  quantity = 1;

  /**
   * Component Configuration
   */
  static config = {
    name: 'ProductInfo',
    components: {
      VariantRadios,
      UpsellProduct,
      PickupAvailability,
      PickupAvailabilityDrawer,
      QuantitySelect: Select,
    },
    refs: [
      'upsellContainer[]',
      'mainPrice',
      'mainPriceWrapper',
      'salePrice',
      'defaultPrice',
      'addToCart',
      'buyButtonFlex',
    ],
  };

  /**
   * input
   * @returns {HTMLInputElement|null}
   */
  get input() {
    // Not rendered when the whole product is sold out.
    if (this.$children.QuantitySelect.length <= 0) {
      return null;
    }

    return this.$children.QuantitySelect[0].$refs.input;
  }

  /**
   * variantSelects
   * @returns {VariantRadios}
   */
  get variantSelects() {
    if (this.$children.VariantRadios.length <= 0) {
      throw new Error('No VariantRadios component found');
    }

    return this.$children.VariantRadios[0];
  }

  /**
   * mounted
   */
  mounted() {
    this.currentVariant = this.$el.querySelector('.product-variant-id');
    this.submitButton = this.$el.querySelector('[type="submit"]');

    this.moveBogosGift();
    this.connectedCallback();
  }

  /**
   * Move the BOGOS gift thumbnail above the buy button container.
   */
  moveBogosGift() {
    const flexDiv = this.$refs.buyButtonFlex;
    if (!flexDiv) return;

    const move = () => {
      const gift = this.$el.querySelector('#sca-gift-thumbnail');
      if (gift) {
        flexDiv.parentNode.insertBefore(gift, flexDiv);
        return true;
      }
      return false;
    };

    if (move()) return;

    this.bogosObserver = new MutationObserver(() => {
      if (move()) {
        this.bogosObserver.disconnect();
        this.bogosObserver = null;
      }
    });
    this.bogosObserver.observe(this.$el, { childList: true, subtree: true });
  }

  /**
   * @returns {number}
   */
  get defaultPrice() {
    return (
      Number.parseFloat(this.$refs.defaultPrice.dataset.default.replace(',', '.')).toFixed(2) *
      this.quantity
    ).toFixed(2);
  }

  /**
   * @returns {number}
   */
  get defaultSalePrice() {
    return this.$refs.salePrice
      ? (
          Number.parseFloat(this.$refs.salePrice.dataset.default.replace(',', '.')).toFixed(2) *
          this.quantity
        ).toFixed(2)
      : 0;
  }

  /**
   *
   * @param {*} param0
   */
  onQuantitySelectClickItem({ args }) {
    const [index] = args;

    this.quantity = index + 1;

    this.updatePriceBasedOnQuantity();
    this.$parent.updateAllQuantities(index, this.$config.name);
  }

  /**
   *
   */
  updatePriceBasedOnQuantity() {
    this.$refs.defaultPrice.dataset.quantity = this.quantity;
    this.$refs.defaultPrice.innerHTML = this.defaultPrice.replace('.', ',');
    if (this.$refs.salePrice) {
      this.$refs.salePrice.innerHTML = this.defaultSalePrice;
    }

    if (this.hasUpsell) {
      const finalPrice = (
        Number.parseFloat(this.defaultPrice) + Number.parseFloat(this.additionnalPrices)
      ).toFixed(2);
      this.$refs.mainPrice.innerHTML = finalPrice.replace('.', ',');

      this.$parent.$children.ProductStickyBar[0].updatePrices(this.additionnalPrices, finalPrice);
    }
    this.$parent.$children.ProductStickyBar[0].updatePriceBasedOnQuantity(
      this.defaultPrice,
      this.defaultSalePrice,
    );
  }

  /**
   *
   */
  onUpsellProductAdd() {
    // No price in the button when the whole product is sold out.
    if (!this.$refs.defaultPrice) {
      return;
    }

    let allUpsellContent = '';
    let additionnalPrices = new Number(0);

    this.hasUpsell = false;

    this.$children.UpsellProduct.forEach((element) => {
      if (element.quantity > 0) {
        allUpsellContent += element.inputsContent;
        additionnalPrices += Number.parseFloat(element.totalPrice);
        this.hasUpsell = true;
      }
    });

    this.additionnalPrices = additionnalPrices;
    this.finalPrice =
      Number.parseFloat(this.defaultPrice) + Number.parseFloat(this.additionnalPrices);

    if (additionnalPrices > 0) {
      this.$refs.mainPrice.innerHTML = this.finalPrice.toFixed(2).replace('.', ',');
      this.$refs.mainPriceWrapper.classList.remove('hidden');
    } else {
      this.$refs.mainPriceWrapper.classList.add('hidden');
    }

    this.$parent.$children.ProductStickyBar[0].updatePrices(
      this.additionnalPrices,
      this.finalPrice,
    );

    this.$refs.upsellContainer.forEach((element) => {
      element.innerHTML = allUpsellContent;
    });
  }

  /**
   * connectedCallback
   */
  connectedCallback() {
    if (!this.input) {
      return;
    }

    this.quantityForm = this.$el.querySelector('.product-form__quantity');

    if (!this.quantityForm) {
      return;
    }
    this.setQuantityBoundries();

    if (
      !this.$el.dataset.originalSection &&
      subscribe &&
      PUB_SUB_EVENTS &&
      PUB_SUB_EVENTS.cartUpdate
    ) {
      this.cartUpdateUnsubscriber = subscribe(
        PUB_SUB_EVENTS.cartUpdate,
        this.fetchQuantityRules.bind(this),
      );
    }

    if (subscribe && PUB_SUB_EVENTS && PUB_SUB_EVENTS.variantChange) {
      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const sectionId = this.$el.dataset.originalSection
          ? this.$el.dataset.originalSection
          : this.$el.dataset.section;
        if (event.data.sectionId !== sectionId) return;
        this.updateQuantityRules(event.data.sectionId, event.data.html);
        this.setQuantityBoundries();
      });
    }
  }

  /**
   * disconnectedCallback
   */
  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
    if (this.variantChangeUnsubscriber) {
      this.variantChangeUnsubscriber();
    }
    if (this.bogosObserver) {
      this.bogosObserver.disconnect();
      this.bogosObserver = null;
    }
  }

  /**
   * setQuantityBoundries
   */
  setQuantityBoundries() {
    const data = {
      cartQuantity: this.input.dataset.cartQuantity
        ? Number.parseInt(this.input.dataset.cartQuantity, 10)
        : 0,
      min: this.input.dataset.min ? Number.parseInt(this.input.dataset.min, 10) : 1,
      max: this.input.dataset.max ? Number.parseInt(this.input.dataset.max, 10) : null,
      step: this.input.step ? Number.parseInt(this.input.step, 10) : 1,
    };

    let { min } = data;
    const max = data.max === null ? data.max : data.max - data.cartQuantity;
    if (max !== null) min = Math.min(min, max);
    if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

    this.input.min = min;
    this.input.max = max;
    this.input.value = min;

    if (publish && PUB_SUB_EVENTS && PUB_SUB_EVENTS.quantityUpdate) {
      publish(PUB_SUB_EVENTS.quantityUpdate);
    }
  }

  /**
   * fetchQuantityRules
   */
  fetchQuantityRules() {
    if (!this.currentVariant || !this.currentVariant.value) {
      return;
    }

    this.$el.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
    fetch(
      `${this.$el.dataset.url}?variant=${this.currentVariant.value}&section_id=${this.$el.dataset.section}`,
    )
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        this.updateQuantityRules(this.$el.dataset.section, html);
        this.setQuantityBoundries();
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        this.$el.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden');
      });
  }

  /**
   * updateQuantityRules
   * @param {*} sectionId
   * @param {*} html
   */
  updateQuantityRules(sectionId, html) {
    const quantityFormUpdated = html.querySelector(`#Quantity-Form-${sectionId}`);
    const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];

    for (const selector of selectors) {
      const current = this.quantityForm.querySelector(selector);
      const updated = quantityFormUpdated.querySelector(selector);

      if (!current || !updated) {
        continue;
      }

      if (selector === '.quantity__input') {
        const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];

        for (const attribute of attributes) {
          const valueUpdated = updated.getAttribute(attribute);
          if (valueUpdated !== null) current.setAttribute(attribute, valueUpdated);
        }
      } else {
        current.innerHTML = updated.innerHTML;
      }
    }
  }
}
